import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/client';
import { useAuth } from '../../auth/AuthContext';
import './LoginPage.css';

const { Title, Text } = Typography;

type LoginFormValues = {
    email: string;
    password: string;
};

function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading, role, roleLoading, roleDenied } = useAuth();
    const [form] = Form.useForm<LoginFormValues>();
    const [submitting, setSubmitting] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const fromPath =
        (location.state as { from?: { pathname?: string } } | undefined)?.from?.pathname ?? '/';

    useEffect(() => {
        if (loading || !user || roleLoading) return;
        const target = fromPath === '/login' ? '/' : fromPath;
        if (roleDenied || !role) {
            navigate('/', { replace: true });
            return;
        }
        navigate(target, { replace: true });
    }, [user, loading, role, roleLoading, roleDenied, fromPath, navigate]);

    const onFinish = async (values: LoginFormValues) => {
        setErrorMessage(null);
        setSubmitting(true);
        try {
            await signInWithEmailAndPassword(auth, values.email.trim(), values.password);
        } catch (err: unknown) {
            const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
            if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
                setErrorMessage('Invalid email or password.');
            } else if (code === 'auth/too-many-requests') {
                setErrorMessage('Too many attempts. Try again later.');
            } else if (code === 'auth/user-disabled') {
                setErrorMessage('This account has been disabled.');
            } else {
                setErrorMessage('Sign-in failed. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const blocking = loading || (user && roleLoading);

    if (blocking) {
        return (
            <div className="login-page login-page--centered">
                <Text type="secondary">{loading ? 'Loading…' : 'Checking permissions…'}</Text>
            </div>
        );
    }

    if (user && !roleLoading && roleDenied) {
        return (
            <div className="login-page login-page--centered">
                <Text type="secondary">Redirecting…</Text>
            </div>
        );
    }

    if (user && role && !roleDenied) {
        return (
            <div className="login-page login-page--centered">
                <Text type="secondary">Redirecting…</Text>
            </div>
        );
    }

    return (
        <div className="login-page">
            <Card className="login-page__card" bordered={false}>
                <Title level={3} className="login-page__title">
                    VehiWheel Admin
                </Title>
                <Text type="secondary" className="login-page__subtitle">
                    Sign in with your Firebase account
                </Text>

                {errorMessage ? (
                    <Alert type="error" message={errorMessage} showIcon className="login-page__alert" />
                ) : null}

                <Form<LoginFormValues>
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    onFinish={onFinish}
                    className="login-page__form"
                >
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Enter your email' },
                            { type: 'email', message: 'Enter a valid email' },
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder="you@company.com"
                            autoComplete="email"
                            size="large"
                        />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Enter your password' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Password"
                            autoComplete="current-password"
                            size="large"
                        />
                    </Form.Item>
                    <Form.Item className="login-page__submit-item">
                        <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
                            Sign in
                        </Button>
                    </Form.Item>
                </Form>

                <Text type="secondary" className="login-page__hint">
                    Accounts are managed in Firebase Authentication (invite users there).
                </Text>
            </Card>
        </div>
    );
}

export default LoginPage;
